import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  HStack,
  Spinner,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { canCreateUsersInContext } from '../../utils/userCreationPermissions';
import {
  getConsultantOrganization,
  findTempPassword,
  updateConsultantOrganization,
} from './api';
import ContextUserCreateModal from '../users/ContextUserCreateModal';
import OrganizationGeneralFields from '../organization/OrganizationGeneralFields';
import OrganizationBillingFields from '../organization/OrganizationBillingFields';

const initialState = {
  name: '',
  contact: '',
  title: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  notes: '',
  billing_same_as_org: true,
  billing_company_name: '',
  billing_address: '',
  billing_zip: '',
  billing_city: '',
  billing_state: '',
  billing_country: '',
  vat_or_tax_number: '',
  currency: '',
  billing_method: '',
  billing_email: '',
  billing_method_other: '',
  reference_person: '',
  reference_number: '',
  cost_center: '',
  billing_notes: '',
  peppol: '',
  gln: '',
  organisationsnummer: '',
};

const ConsultantOrganizationEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useUser();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const canCreateOrganizationUsers = canCreateUsersInContext(user, { type: 'organization', id: Number(id) });

  const [form, setForm] = useState(initialState);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [createdUserEmail, setCreatedUserEmail] = useState(null);

  const createUserModal = useDisclosure();

  useEffect(() => {
    const loadOrganization = async () => {
      setIsLoading(true);
      try {
        const organization = await getConsultantOrganization(id);
        setForm({
          name: organization?.name || '',
          contact: organization?.contact || '',
          title: organization?.title || '',
          email: organization?.email || '',
          phone: organization?.phone || '',
          address: organization?.address || '',
          city: organization?.city || '',
          state: organization?.state || '',
          zip: organization?.zip || organization?.postcode || '',
          country: organization?.country || '',
          notes: organization?.notes || '',
          billing_same_as_org: organization?.billing_same_as_org ?? true,
          billing_company_name: organization?.billing_company_name || '',
          billing_address: organization?.billing_address || '',
          billing_zip: organization?.billing_zip || '',
          billing_city: organization?.billing_city || '',
          billing_state: organization?.billing_state || '',
          billing_country: organization?.billing_country || '',
          vat_or_tax_number: organization?.vat_or_tax_number || '',
          currency: organization?.currency || '',
          billing_method: organization?.billing_method || '',
          billing_email: organization?.billing_email || '',
          billing_method_other: organization?.billing_method_other || '',
          reference_person: organization?.reference_person || '',
          reference_number: organization?.reference_number || '',
          cost_center: organization?.cost_center || '',
          billing_notes: organization?.billing_notes || '',
          peppol: organization?.peppol || '',
          gln: organization?.gln || '',
          organisationsnummer: organization?.organisationsnummer || '',
        });
      } catch (error) {
        console.error('Error loading organization:', error);
        toast({ title: 'Could not load school', status: 'error', duration: 4000, isClosable: true });
      } finally {
        setIsLoading(false);
      }
    };

    loadOrganization();
  }, [id, toast]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    try {
      await updateConsultantOrganization(id, form);
      toast({ title: 'School updated', status: 'success', duration: 3000, isClosable: true });
      navigate('/app/consultant/organizations');
    } catch (error) {
      console.error('Error updating organization:', error);
      toast({ title: 'Could not update school', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <HStack justify="center" py={12}>
        <Spinner size="lg" />
      </HStack>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      <HStack justify="space-between" align={{ base: 'start', md: 'center' }} mb={1} flexWrap="wrap">
        <Heading size="lg">Edit school</Heading>
        {canCreateOrganizationUsers ? (
          <Button size="sm" variant="outline" colorScheme="blue" onClick={createUserModal.onOpen}>
            Create user
          </Button>
        ) : null}
      </HStack>
      <Text color="gray.500" mb={5}>Update core school details.</Text>

      {createdUserEmail ? (
        <Alert status="success" borderRadius="md" mb={4}>
          <AlertIcon />
          <AlertDescription>Created user: {createdUserEmail}</AlertDescription>
        </Alert>
      ) : null}

      <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <VStack align="stretch" spacing={4}>
              <OrganizationGeneralFields values={form} onChange={handleChange} sectionBg="gray.50" />
              <OrganizationBillingFields
                values={form}
                onChange={handleChange}
                onBillingSameAsOrgChange={(value) => setForm((prev) => ({ ...prev, billing_same_as_org: value }))}
                sectionBg="gray.50"
              />

              <HStack justify="flex-end">
                <Button variant="ghost" onClick={() => navigate('/app/consultant/organizations')}>
                  Cancel
                </Button>
                <Button type="submit" colorScheme="blue" variant="outline" isLoading={isSaving}>
                  Save
                </Button>
              </HStack>
            </VStack>
          </form>
        </CardBody>
      </Card>

      <ContextUserCreateModal
        isOpen={createUserModal.isOpen}
        onClose={createUserModal.onClose}
        context={{ type: 'organization', id: Number(id), namespace: 'consultant' }}
        title="Create school user"
        submitLabel="Create user"
        onCreated={async (response, payload) => {
          const tempPassword = findTempPassword(response?.data);
          setCreatedUserEmail(
            tempPassword
              ? `${payload.email} (temporary password generated)`
              : payload.email
          );
        }}
      />
    </Box>
  );
};

export default ConsultantOrganizationEdit;
